// ── StoreHealthScore — 매장 건강 점수 100점 위젯 ───────────────
// 홈 대시보드 메인 — 도넛 차트 + 7개 지표 분해 (펼치기)
import React, { useState } from 'react';

const fmt = n => Math.round(Number(n) || 0).toLocaleString('ko-KR');

export default function StoreHealthScore({ score, isMobile, onNavigate }) {
  const [expanded, setExpanded] = useState(false);
  if (!score) return null;

  const { total, indicators, color, label, message, investment } = score;
  const radius = isMobile ? 56 : 70;
  const stroke = 14;
  const size = (radius + stroke) * 2;
  const circumference = 2 * Math.PI * radius;
  const dashArray = (total / 100) * circumference;

  return (
    <div style={{
      background: '#fff',
      borderRadius: 16,
      padding: isMobile ? '20px 16px' : '24px 28px',
      marginBottom: 18,
      boxShadow: '0 3px 18px rgba(0,0,0,0.08)',
      border: `2px solid ${color}`,
    }}>
      {/* 상단 — 도넛 + 메시지 */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? 16 : 24,
        flexWrap: 'wrap',
      }}>
        {/* 도넛 차트 */}
        <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
          <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
            <circle cx={size/2} cy={size/2} r={radius}
              fill="none" stroke="#f3f4f6" strokeWidth={stroke} />
            <circle cx={size/2} cy={size/2} r={radius}
              fill="none" stroke={color} strokeWidth={stroke}
              strokeDasharray={`${dashArray} ${circumference}`}
              strokeLinecap="round"
              style={{ transition: 'stroke-dasharray 0.6s ease' }} />
          </svg>
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{ fontSize: isMobile ? 32 : 40, fontWeight: 900, color, lineHeight: 1 }}>
              {total}
            </div>
            <div style={{ fontSize: isMobile ? 11 : 12, color: '#9ca3af', marginTop: 2 }}>
              / 100점
            </div>
          </div>
        </div>

        {/* 메시지 */}
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', marginBottom: 4, letterSpacing: '0.04em' }}>
            매장 건강 점수
          </div>
          <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color, marginBottom: 6 }}>
            {label}
          </div>
          <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, marginBottom: 10 }}>
            {message}
          </div>
          <button onClick={() => setExpanded(e => !e)}
            style={{
              padding: '6px 14px', borderRadius: 7,
              border: '1.5px solid #e5e7eb', background: '#fff',
              color: '#374151', fontSize: 12, fontWeight: 700,
              cursor: 'pointer',
            }}>
            {expanded ? '▴ 지표 접기' : '▾ 7개 지표 상세 보기'}
          </button>
        </div>
      </div>

      {/* 지표 분해 */}
      {expanded && (
        <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px dashed #e5e7eb' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 10,
          }}>
            {indicators.map(ind => <IndicatorRow key={ind.id} ind={ind} />)}
          </div>

          {/* V170 투자 회수 · 누적 순이익률 (참고 — 점수에는 안 들어감) */}
          {investment && (
            <div data-health-invest style={{ marginTop: 10, background: '#F0FDFA', borderRadius: 10, padding: '10px 14px', borderLeft: '4px solid #0F766E', fontSize: 12, color: '#134E4A', lineHeight: 1.7 }}>
              <div style={{ fontWeight: 800, fontSize: 13 }}>🏗️ 투자 회수 · 누적 손익 <span style={{ fontWeight: 500, color: '#6b7280', fontSize: 11 }}>(참고)</span></div>
              <div>누적 순이익률 <b>{investment.cumMargin == null ? '—' : `${investment.cumMargin}%`}</b> ({investment.months}개월) · 투자 회수율 <b>{investment.rate == null ? '—' : `${investment.rate}%`}</b></div>
              <div>{investment.recoveredAt ? `✅ ${investment.recoveredAt} 회수 완료`
                : investment.expected ? `📅 예상 회수 ${investment.expected} (약 ${investment.monthsLeft}개월 남음)`
                : '⚠️ 최근 영업현금으로는 회수 시점을 계산할 수 없어요 — 손익 화면에서 확인해 주세요'}</div>
            </div>
          )}

          {/* CTA */}
          <div style={{ marginTop: 14, fontSize: 11, color: '#9ca3af', textAlign: 'center', lineHeight: 1.6 }}>
            💡 점수가 낮은 지표를 개선하면 자동으로 점수가 올라가요. 매일 들어와서 데이터 입력하세요.
          </div>
        </div>
      )}
    </div>
  );
}

// ── 단일 지표 행 ──
function IndicatorRow({ ind }) {
  const pct = ind.score / ind.weight;  // 0 ~ 1
  const color = pct >= 0.8 ? '#FF6F0F' : pct >= 0.5 ? '#EA580C' : '#52525B';
  const valueText = ind.value === null ? '—'
    : ind.id === 'diligence' ? `${fmt(ind.value)} 회`
    : ind.id === 'growth' || ind.id === 'netMargin' || ind.id === 'foodCost' || ind.id === 'laborCost' || ind.id === 'inventory' || ind.id === 'stability'
      ? `${ind.value.toFixed(1)}%`
      : fmt(ind.value);

  return (
    <div style={{
      background: '#fafbfc',
      borderRadius: 10,
      padding: '10px 14px',
      borderLeft: `4px solid ${color}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>{ind.label}</span>
        <span style={{ fontSize: 13, fontWeight: 800, color }}>
          {ind.score.toFixed(1)} / {ind.weight}
        </span>
      </div>
      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
        {ind.value !== null ? `값: ${valueText}` : ''} {ind.hint}
      </div>
      {/* 진행 바 */}
      <div style={{ height: 4, background: '#f3f4f6', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${Math.max(2, pct * 100)}%`,
          background: color, borderRadius: 2, transition: 'width 0.4s',
        }} />
      </div>
    </div>
  );
}
